import { action, makeObservable, observable, toJS } from 'mobx';
import React, { CSSProperties } from 'react';

export type ElementConfigType = {
  nodeId: string;
  parentNodeId: string;
  parentNodeIsBody?: boolean;
  componentId: string;
  componentName: string;

  config: Record<string, string>; // TODO
  styles: CSSProperties;
  elements: ElementConfigType[];
  index: number; // 数量
  schema?: any; // 类型定义好在确认
  hidden?: boolean;
  privateSchema?: Record<string, any>; // 类型定义好在确认
  isDevelopment?: boolean; // 是否预览模式
  isRenderHtml?: boolean; // 是否 renderHtml
  ComponentBasicRender?: any;
  ComponentItemRender?: any;
};

export type ElementConfigWithoutElementsType = Omit<ElementConfigType, 'elements'>;

export interface PageConfigType {
  elements: ElementConfigType[];
  moElements?: ElementConfigType[];
}

export class PageEditorStore {
  configNode: ElementConfigType | null = null;
  hoverNode: ElementConfigType | null = null;
  forceUpdate = 0;
  isDevelopment = true;
  isRenderHtml = true;
  model: 'elements' = 'elements'; // 当前模式
  folderId: number | null = null; //  文件id
  showRichTextEditorDialog = false; // 富文本编辑器弹窗

  page: PageConfigType = {
    elements: [
      {
        nodeId: `${new Date().getTime() + 1}`,
        parentNodeId: '0',
        componentId: 'body-component',
        componentName: '页面容器',
        hidden: true,
        index: 1,
        config: {},
        styles: {},
        privateSchema: {
          maxWidth: '1354px', // 初始值
        },
        elements: [],
      },
    ],
  };

  constructor() {
    makeObservable(this, {
      configNode: observable,
      hoverNode: observable,
      isDevelopment: observable,
      isRenderHtml: observable,
      model: observable,
      folderId: observable,
      showRichTextEditorDialog: observable,
      setConfigNode: action,
      setHoverNode: action,
      setIsDevelopment: action,
      setModel: action,
      page: observable,
      setConfigNodeStyle: action,
      setConfigNodeOwnAttribute: action,
      forceUpdate: observable,
      addNode: action,
    });
  }

  setModel(model: 'elements') {
    this.model = model;
    this.forceUpdate++;
  }

  // 设置页面
  setPage(page: PageConfigType) {
    this.page = toJS(page);
  }

  setConfigNode(node: ElementConfigType | null) {
    this.configNode = toJS(node);
  }

  setFolderId(id: number | null) {
    this.folderId = id;
  }
  setHoverNode(node: ElementConfigType | null) {
    this.hoverNode = node ? toJS(node) : node;
  }
  setIsDevelopment(preview: boolean) {
    this.isDevelopment = preview;
    this.forceUpdate++;
  }
  setIsRenderHtml(isRenderHtml: boolean = true) {
    this.isRenderHtml = isRenderHtml;
    this.forceUpdate++;
  }

  setShowRichTextEditorDialog(show: boolean) {
    this.showRichTextEditorDialog = show;
  }

  // 设置 css 属性
  setConfigNodeStyle(style: CSSProperties) {
    if (this.configNode) {
      const { nodeId } = this.configNode;
      const matchNode = this.loopSearchNode(this.page[this.model], nodeId);

      if (matchNode) {
        matchNode.styles = Object.assign({}, matchNode.styles, style);
        this.forceUpdate++;
      }
    }
  }

  // 设置自己的特有 属性
  setConfigNodeOwnAttribute(privateSchema: CSSProperties) {
    if (this.configNode) {
      const { nodeId } = this.configNode;
      const matchNode = this.loopSearchNode(this.page[this.model], nodeId);
      if (matchNode) {
        matchNode.privateSchema = Object.assign({}, matchNode.privateSchema, privateSchema);
        this.forceUpdate++;
      }
    }
  }

  // 添加
  addNode(nodeId: string, newNode: ElementConfigType) {
    const matchNode = this.loopSearchNode(this.page[this.model], nodeId);
    if (matchNode) {
      matchNode.elements.push(newNode);

      this.forceUpdate++;
    } else {
      console.warn('add 没找到对应Node: ', nodeId);
    }
  }

  // 减少 删除最后一个 Node
  reduceNode(nodeId: string) {
    const matchNode = this.loopSearchNode(this.page[this.model], nodeId);
    if (matchNode) {
      matchNode.elements.pop();

      this.forceUpdate++;
    } else {
      console.warn('reduce 没找到对应Node: ', nodeId);
    }
  }

  // 删除
  deleteNode(nodeId: string) {
    const matchNode = this.loopSearcParentNode(this.page[this.model], nodeId);
    if (matchNode) {
      matchNode.elements = matchNode?.elements?.filter((d) => d.nodeId !== nodeId);
      this.forceUpdate++;
    } else {
      console.warn('删除 没找到对应Node: ', nodeId);
    }
  }

  // 寻找当前容器
  loopSearchNode(elements: ElementConfigType[], nodeId: string): ElementConfigType | null {
    for (const element of elements) {
      if (element.nodeId === nodeId) {
        return element;
      } else {
        const nextLoop = this.loopSearchNode(element.elements, nodeId);
        if (nextLoop) {
          return nextLoop;
        }
      }
    }
    return null;
  }

  // 寻找父级
  loopSearcParentNode(elements: ElementConfigType[], nodeId: string): ElementConfigType | null {
    let parentNode: ElementConfigType | null = null;
    for (let i = 0; i < elements.length; i++) {
      const node = elements[i];
      if (node.elements) {
        if (node.elements.some((item) => item?.nodeId === nodeId)) {
          parentNode = node;
        } else if (this.loopSearcParentNode(node.elements, nodeId)) {
          parentNode = this.loopSearcParentNode(node.elements, nodeId);
        }
      }
    }
    return parentNode;
  }

  // 寻找当前容器 在父级下一级排第几个
  loopSearcNodeIndex(elements: ElementConfigType[], nodeId: string): number {
    const nodeIndex = elements[0].elements.findIndex((d) => d.nodeId === nodeId);
    return nodeIndex;
  }
}

export const PageEditorStoreContext = React.createContext<PageEditorStore>(null as any);
